import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Form,
  Label,
  FormText,
  Nav,
  NavItem,
  NavLink,
  Alert,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import usePermissions from "../../../Components/Hooks/usePermissions";
import classnames from "classnames";
import { isEqual } from 'lodash'; // A handy utility for deep object comparison

// Make sure your thunk path is correct
import { fetchAllPrompts, savePrompt } from "../../../slices/AIPrompts/thunk";

const MAX_PROMPT_LENGTH = 10000;
const INITIAL_FORM_STATE = { Name: "", PromptText: "" };

function AIPrompts() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // --- STATE MANAGEMENT ---
  // The currently selected prompt object from the list
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  // Flag to indicate if we are in "create" mode
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  // Form data for the editor (name and text)
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  // Original form data to compare against for changes
  const [originalFormData, setOriginalFormData] = useState(INITIAL_FORM_STATE);

  const { prompts, loading, saveLoading, error } = useSelector((state) => ({
    prompts: state.AIPrompt.prompts || [],
    loading: state.AIPrompt.loading,
    saveLoading: state.AIPrompt.saveLoading || false,
    error: state.AIPrompt.error,
  }));

  const permission = usePermissions(2, 10);

  // --- EFFECTS ---
  // 1. Fetch all prompts on initial component load
  useEffect(() => {
    dispatch(fetchAllPrompts());
  }, [dispatch]);
  
  // 2. Handle errors from the Redux store
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  
  // --- HANDLERS ---
  const handleSelectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setFormData({ Name: prompt.PromptName, PromptText: prompt.PromptText });
    setOriginalFormData({ Name: prompt.Name, PromptText: prompt.PromptText });
    setIsCreatingNew(false);
  };

  const handleAddNew = () => {
    setSelectedPrompt(null); // Deselect any active prompt
    setFormData(INITIAL_FORM_STATE);
    setOriginalFormData(INITIAL_FORM_STATE);
    setIsCreatingNew(true);
  };

  const handleCancel = () => {
    if (isCreatingNew) {
      // If we were creating, just clear the form and exit create mode
      setIsCreatingNew(false);
    } else {
      // If editing, revert to the original data of the selected prompt
      setFormData(originalFormData);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!permission || !permission.CanEdit) {
      toast.error(t("You do not have permission to save prompts."));
      return;
    }
    
    // Basic validation
    if (!formData.Name.trim()) {
      toast.error(t("Prompt Name is required."));
      return;
    }

    const payload = {
      PromptName: formData.Name.trim(),
      PromptText: formData.PromptText,
    };

    // If we are editing an existing prompt, add its ID to the payload
    if (selectedPrompt && selectedPrompt.ID) {
      payload.ID = selectedPrompt.ID;
    }

    dispatch(savePrompt(payload)).then((action) => {
      // After save, refetch all prompts to get the updated list
      // This handles both new creations and updates gracefully
      if (action.type.endsWith('fulfilled')) {
        dispatch(fetchAllPrompts());
        setIsCreatingNew(false); // Exit create mode on successful save
        
        // If it was a new prompt, find and select it in the new list
        if (!payload.ID) {
            const newPrompt = action.payload; // Assuming thunk returns the saved object
            if (newPrompt && newPrompt.ID) {
                // We need to wait for the fetchAllPrompts to complete. 
                // A better way is to update the local state directly, but re-fetching is simpler.
                // For now, we'll just reset the selection.
                setSelectedPrompt(null);
            }
        } else {
            // If we updated an existing prompt, update the original data to match
            setOriginalFormData(formData);
        }
      }
    });
  };

  // --- RENDER LOGIC ---
  const hasChanges = !isEqual(formData, originalFormData);
  const isSaveDisabled = saveLoading || !hasChanges;
  const showEditor = selectedPrompt || isCreatingNew;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("Manage AI Prompts")} />
        <Row>
          {/* --- LEFT PANE: PROMPT LIST --- */}
          <Col lg={4}>
            <Card>
              <CardHeader className="d-flex align-items-center">
                <h5 className="card-title mb-0 flex-grow-1">{t("AvailablePrompts")}</h5>
                {/* <Button color="primary" size="sm" onClick={handleAddNew} disabled={!permission?.CanEdit}>
                  <i className="ri-add-line align-bottom me-1"></i> {t("Add New")}
                </Button> */}
              </CardHeader>
              <CardBody>
                {loading ? (
                    <div className="text-center py-5">
                      <Spinner size="sm" /> <span className="ms-2">{t("Loading")}</span>
                    </div>
                ) : (
                  <Nav className="flex-column nav-pills" role="tablist">
                    {prompts.map((prompt) => (
                      <NavItem key={prompt.ID}>
                        <NavLink
                          href="#"
                          className={classnames({ active: selectedPrompt?.ID === prompt.ID })}
                          onClick={() => handleSelectPrompt(prompt)}
                        >
                          {prompt.PromptName}
                        </NavLink>
                      </NavItem>
                    ))}
                    {prompts.length === 0 && !loading && (
                        <p className="text-muted text-center">{t("No prompts found. Click 'Add New' to create one.")}</p>
                    )}
                  </Nav>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* --- RIGHT PANE: PROMPT EDITOR --- */}
          <Col lg={8}>
            {showEditor ? (
              <Form onSubmit={handleSave}>
                <Card>
                  <CardHeader className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-0">
                        {isCreatingNew ? t("Create New Prompt") : `${t("Edit Prompt:")} ${originalFormData.Name}`}
                      </h5>
                    </div>
                    <div className="flex-shrink-0 d-flex gap-2">
                      <Button color="light" onClick={handleCancel} disabled={isSaveDisabled}>
                        {t("Cancel")}
                      </Button>
                      <Button type="submit" color="primary" disabled={isSaveDisabled}>
                        {saveLoading ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line align-bottom me-1"></i>}
                        {t("Save")}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label htmlFor="prompt-name-input" className="form-label">
                        {t("Prompt Name")}
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="prompt-name-input"
                        placeholder={t("e.g., General Support Prompt")}
                        value={formData.Name}
                        name="Name"
                        onChange={handleFormChange}
                        disabled={saveLoading}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="prompt-text-area" className="form-label">
                        {t("Prompt Configuration")}
                      </Label>
                      <Input
                        type="textarea"
                        className="form-control"
                        id="prompt-text-area"
                        rows="15"
                        placeholder={t("Enter the AI system prompt here")}
                        value={formData.PromptText}
                        name="PromptText"
                        onChange={handleFormChange}
                        disabled={saveLoading}
                        maxLength={MAX_PROMPT_LENGTH}
                      />
                      <div className="d-flex justify-content-end align-items-center mt-2">
                        <FormText color={formData.PromptText.length > MAX_PROMPT_LENGTH * 0.9 ? 'danger' : 'muted'}>
                          {formData.PromptText.length} / {MAX_PROMPT_LENGTH}
                        </FormText>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Form>
            ) : (
                <Card>
                    <CardBody>
                        <div className="text-center py-5">
                            <div className="avatar-lg mx-auto mt-2">
                                <div className="avatar-title bg-light text-primary display-3 rounded-circle">
                                    <i className="ri-edit-2-fill"></i>
                                </div>
                            </div>
                            <div className="mt-4 pt-2">
                                <h4>{t("NoPromptSelected")}</h4>
                                {/* <p className="text-muted">{t("Please select a prompt from the list on the left to view or edit it, or create a new one.")}</p> */}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default AIPrompts;